import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Utils } from '../../../../../shared/utils';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TierService } from '../../../../shared/services/tier.service';
import { StatusPillVariant } from '../../../../../shared/ui/status-pill/status-pill.component';
import {
  FinancialInvestmentDeposit,
  FinancialInvestmentRow,
  paidLabel,
  paidVariant,
  roundMoney,
} from '../../models/financial-investment.model';
import { FinancialInvestmentEntityService } from '../../services/financial-investment-entity.service';
import { depositDetails, FinancialInvestmentPayDialogComponent } from '../financial-investment-pay-dialog/financial-investment-pay-dialog.component';

interface PaymentLine {
  uid: string;
  date: string | Date;
  concept: string;
  meta: string;
  amount: number;
  source: FinancialInvestmentDeposit;
}

const MAX_LINES = 5;

@Component({
  selector: 'app-financial-investment-detail',
  templateUrl: './financial-investment-detail.component.html',
  styleUrls: ['./financial-investment-detail.component.scss'],
  standalone: false,
})
export class FinancialInvestmentDetailComponent implements OnChanges {
  @Input() row!: FinancialInvestmentRow;
  @Input() refreshToken = 0;
  @Output() changed = new EventEmitter<void>();
  @Output() edit = new EventEmitter<void>();

  readonly maxLines = MAX_LINES;

  loading = true;
  error = false;
  lines: PaymentLine[] = [];
  count = 0;
  paidTotal = 0;
  commissionsTotal = 0;

  private loadedSignature = '';

  constructor(
    private entity: FinancialInvestmentEntityService,
    private dialog: MatDialog,
    private confirm: ConfirmService,
    private notify: NotificationService,
    private tier: TierService,
  ) { }

  get percent(): number {
    const value = Number(this.row && this.row.investmentValue) || 0;
    if (value <= 0) return 0;
    return Math.min(100, Math.max(0, (Number(this.row.paidAmount) || 0) / value * 100));
  }

  get statusVariant(): StatusPillVariant {
    return paidVariant(this.row);
  }

  get statusLabel(): string {
    return paidLabel(this.row);
  }

  get accountLabel(): string {
    if (!this.row) return '—';
    return [this.row.accountNumber, this.row.accountName].filter(Boolean).join(' · ') || '—';
  }

  hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  ngOnChanges(): void {
    if (!this.row || !this.row.uid) return;
    const signature = [this.row.uid, this.refreshToken].join('|');
    if (signature === this.loadedSignature) return;
    this.loadedSignature = signature;
    this.load(this.row.uid);
  }

  retry(): void {
    if (!this.row || !this.row.uid) return;
    this.load(this.row.uid);
  }

  add(): void {
    this.openDialog();
  }

  manage(): void {
    this.openDialog();
  }

  editLine(line: PaymentLine): void {
    this.openDialog(line.uid);
  }

  deleteLine(line: PaymentLine): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.confirm.open({
      title: 'Eliminar pago',
      subtitle: [this.row.investmentDescription, this.row.accountNumber].filter(Boolean).join(' · ') || undefined,
      message: 'Se eliminará el pago y su asiento contable. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: depositDetails(line.source),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando pago…',
      errorText: 'No se pudo eliminar el pago',
      action: () => this.entity.deleteDeposit(line.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Pago eliminado correctamente');
      this.changed.emit();
    });
  }

  trackByUid(_: number, line: PaymentLine): string {
    return line.uid;
  }

  private openDialog(editUid?: string): void {
    if (!this.tier.requireDocumentsTier()) return;
    FinancialInvestmentPayDialogComponent.open(this.dialog, {
      investment: {
        uid: this.row.uid,
        investmentDescription: this.row.investmentDescription,
        investmentValue: this.row.investmentValue,
        accountNumber: this.row.accountNumber,
      },
      editUid,
    }).subscribe(changed => { if (changed) this.changed.emit(); });
  }

  private load(uid: string): void {
    this.loading = !this.lines.length;
    this.error = false;

    this.entity.deposits(uid)
      .then(list => {
        if (!this.row || uid !== this.row.uid) return;
        const deposits = (Array.isArray(list) ? list : [])
          .slice()
          .sort((a, b) => new Date(b.depositDate).getTime() - new Date(a.depositDate).getTime());
        this.count = deposits.length;
        this.paidTotal = roundMoney(deposits.reduce((acc, d) => acc + (Number(d.amount) || 0), 0));
        this.commissionsTotal = roundMoney(deposits.reduce((acc, d) => acc + (Number(d.commissions) || 0), 0));
        this.lines = deposits.slice(0, MAX_LINES).map(deposit => ({
          uid: deposit.uid,
          date: deposit.depositDate,
          concept: deposit.typePay || 'Sin forma de pago',
          meta: [
            deposit.bankAccountInfo || '',
            deposit.commissions ? 'Comisión ' + Utils.FormatMoney(deposit.commissions) + ' €' : '',
            deposit.conciliationId ? 'Conciliado' : '',
          ].filter(Boolean).join(' · '),
          amount: Number(deposit.amount) || 0,
          source: deposit,
        }));
      })
      .catch(() => { this.error = true; })
      .then(() => { this.loading = false; });
  }
}
